import { css } from '@emotion/react';
import { UseComboboxPropGetters } from 'downshift';
import { forwardRef } from 'react';
import { InterpreterFrom } from 'xstate';

import { Icon, Text } from '~src/commons/components';
import { colors } from '~src/theme/colors';
import { fontStyles } from '~src/theme/fontStyles';

import { ComboboxListItem } from './autocomplete';
import { AutocompleteMachine } from './machine';
import { PoweredByFooter } from './powered-by-footer';

interface ResultsProps {
  highlightedIndex: number;
  getItemProps: UseComboboxPropGetters<ComboboxListItem>['getItemProps'];
  getMenuProps: UseComboboxPropGetters<ComboboxListItem>['getMenuProps'];
  listItems: ComboboxListItem[];
  machineService: InterpreterFrom<AutocompleteMachine>;
  isOpen: boolean;
}

export const Results = forwardRef<HTMLUListElement, ResultsProps>(
  ({ highlightedIndex, getItemProps, getMenuProps, isOpen, listItems, machineService }, ref) => {
    const { matches, context } = machineService.getSnapshot();
    const arePlacesEmpty = matches('focused.empty');

    return (
      <div css={styles.listWrapper}>
        <div css={styles.listContainer(isOpen && listItems.length > 0)}>
          <ul css={styles.list} {...getMenuProps({ ref })}>
            {isOpen &&
              listItems.map((item, index) =>
                item === 'SHOW_ALL' ? (
                  <li
                    key={item}
                    css={styles.listItem({
                      isFirstChild: index === 0,
                      isLastChild: true,
                      isHighlighted: highlightedIndex === index,
                    })}
                    {...getItemProps({ item, index })}
                  >
                    <Text css={styles.openManualAddressButton}>Show all addresses</Text>
                  </li>
                ) : (
                  <li
                    css={styles.listItem({
                      isFirstChild: index === 0,
                      isHighlighted: highlightedIndex === index,
                    })}
                    key={'container_id' in item ? item.container_id : item.id}
                    {...getItemProps({ item: item, index })}
                  >
                    <div css={styles.textWrapper}>
                      <Text css={styles.mainText}>{item.description.main}</Text>
                      <Text css={styles.secondaryText}>{item.description.secondary}</Text>
                    </div>
                    {'container_id' in item ? (
                      <Icon type="caretRight" color="darkGrey" css={styles.caretIcon} />
                    ) : null}
                  </li>
                )
              )}
          </ul>
          {isOpen && listItems.length > 0 && !arePlacesEmpty && (
            <PoweredByFooter provider={context.provider} />
          )}
        </div>
      </div>
    );
  }
);

Results.displayName = 'Results';

const styles = {
  list: css({
    background: 'white',
    padding: 0,
    margin: 0,
    maxHeight: '250px',
    overflowY: 'auto',
    '&:empty': {
      border: 0,
      opacity: 0,
    },
  }),
  listContainer: (visible: boolean) => ({
    ...(visible && {
      boxShadow: '0px 2px 8px 0px #0000001A, 2px 2px 8px 0px #0000001A',
      borderRadius: '3px',
      border: '1px solid #E8E8E8',
    }),
  }),
  caretIcon: css({ marginLeft: 'auto' }),
  listWrapper: css({
    width: '100%',
    zIndex: 999,
    background: 'white',
    position: 'absolute',
    top: 'calc(100% + 8px)',
    borderRadius: '3px',
  }),
  mainText: css({
    marginBottom: '2px',
    ...fontStyles.px16_20,
    color: colors.darkGrey,
  }),
  secondaryText: css({
    ...fontStyles.px14_20,
  }),
  listItem: ({
    isFirstChild,
    isLastChild = false,
    isHighlighted,
  }: {
    isFirstChild: boolean;
    isLastChild?: boolean;
    isHighlighted: boolean;
  }) =>
    css({
      background: isHighlighted ? 'hsl(0, 0%, 98%)' : 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      height: isLastChild ? '40px' : '52px',
      padding: '8px 20px 8px 16px',
      marginTop: isFirstChild ? '8px' : '4px',
      marginBottom: 0,
    }),
  textWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    fontVariationSettings: "'wght' 400",
  }),
  openManualAddressButton: css({
    ...fontStyles.px12_16,
    fontVariationSettings: '"wght" 500',
    marginLeft: '4px',
  }),
};
